import { Coins, Lock, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import type { Column } from "@/components/shared/data-table";
import { DataTable } from "@/components/shared/data-table";
import { PageHeader } from "@/components/shared/page-header";
import { StatusToggleBadge } from "@/components/shared/status-toggle-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useHasPermission } from "@/hooks/use-auth";
import { useServerTable } from "@/hooks/use-server-table";
import { useDeleteWorkspace, useUpdateWorkspace } from "@/hooks/use-workspaces";
import { queryKeys } from "@/lib/query-keys";
import { workspaceService } from "@/services/workspace";

import { WorkspaceDialog } from "./workspace-dialog";

import type { Workspace } from "@artisancode/api-types";

export function WorkspaceList() {
  const canCreate = useHasPermission("workspaces.create");
  const canUpdate = useHasPermission("workspaces.update");
  const canDelete = useHasPermission("workspaces.delete");

  const navigate = useNavigate();
  const { mutate: update } = useUpdateWorkspace();
  const { mutate: deleteWorkspace, isPending: isDeleting } =
    useDeleteWorkspace();
  const [deleting, setDeleting] = useState<Workspace | null>(null);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Workspace | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const table = useServerTable<Workspace>({
    queryKey: (params) => queryKeys.workspaces.list(params),
    fetcher: (params) => workspaceService.list(params),
    pageSize: 10,
  });

  function toggleActive(item: Workspace) {
    if (item.isMain || !canUpdate || updatingId === item.id) return;
    setUpdatingId(item.id);
    update(
      { id: item.id, isActive: !item.isActive },
      {
        onSuccess: () =>
          toast.success(item.isActive ? "Dinonaktifkan." : "Diaktifkan."),
        onError: () => toast.error("Gagal mengubah status workspace."),
        onSettled: () => setUpdatingId(null),
      },
    );
  }

  function handleDelete(item: Workspace) {
    setDeleting(item);
  }

  function confirmDelete() {
    if (!deleting) return;
    deleteWorkspace(deleting.id, {
      onSuccess: () => toast.success("Workspace berhasil dihapus."),
      onError: () => toast.error("Gagal menghapus workspace."),
      onSettled: () => setDeleting(null),
    });
  }

  const columns: Column<Workspace>[] = [
    {
      key: "name",
      label: "Nama Workspace",
      render: (w) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{w.name}</span>
          {w.isMain && (
            <Badge variant="outline" className="gap-1 text-[10px]">
              <Lock className="h-3 w-3" />
              Utama
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: "slug",
      label: "Slug",
      render: (w) => (
        <span className="text-sm text-muted-foreground">{w.slug}</span>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (w) => (
        <StatusToggleBadge
          isActive={w.isActive}
          isLoading={updatingId === w.id}
          disabled={!canUpdate || w.isMain}
          onClick={() => toggleActive(w)}
        />
      ),
    },
  ];

  function openAdd() {
    setEditing(null);
    setOpen(true);
  }

  function openEdit(item: Workspace) {
    setEditing(item);
    setOpen(true);
  }

  return (
    <div>
      <PageHeader
        title="Workspace"
        description="Kelola workspace (tenant) pada platform."
        action={
          <Button size="sm" onClick={openAdd} disabled={!canCreate}>
            <Plus className="mr-1 h-4 w-4" />
            Tambah
          </Button>
        }
      />
      <DataTable
        data={table.items}
        loadedData={table.loadedItems}
        columns={columns}
        loading={table.loading}
        searchPlaceholder="Cari workspace..."
        query={table.query}
        onQueryChange={table.onQueryChange}
        page={table.page}
        totalPages={table.totalPages}
        totalCount={table.totalCount}
        onPageChange={table.onPageChange}
        hasMore={table.hasMore}
        onLoadMore={table.onLoadMore}
        actions={(item) => (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              title="Atur Gaji & Bonus Rider"
              disabled={!canUpdate}
              onClick={() =>
                navigate(`/settings/workspaces/${item.id}/gaji-bonus-rider`)
              }
            >
              <Coins className="h-4 w-4 text-amber-500" />
            </Button>
            {/* Disabled for now — depends on notification_channel (see rest.ts).
            <Button
              variant="ghost"
              size="icon"
              title="Atur Notifikasi Workspace"
              disabled={!canUpdate}
              onClick={() =>
                navigate(`/settings/workspaces/${item.id}/notifications`)
              }
            >
              <Bell className="h-4 w-4 text-sky-500" />
            </Button>
            */}
            <Button
              variant="ghost"
              size="icon"
              title="Edit Workspace"
              disabled={!canUpdate}
              onClick={() => openEdit(item)}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              title="Hapus Workspace"
              disabled={item.isMain || !canDelete}
              onClick={() => handleDelete(item)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
      />

      <WorkspaceDialog open={open} onOpenChange={setOpen} editing={editing} />
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(next) => !next && setDeleting(null)}
        title="Hapus workspace?"
        description={
          deleting
            ? `Workspace "${deleting.name}" akan dihapus dan tidak bisa dikembalikan.`
            : undefined
        }
        onConfirm={confirmDelete}
        isConfirming={isDeleting}
      />
    </div>
  );
}
